import { z } from 'zod';
import { RoleSchema, type Role } from '@revcall/shared';
import { apiClient } from '../client';

/**
 * Admin + member-management endpoints.
 *
 * Backend routes:
 *   - /api/admin/organisations          (super_admin only)
 *   - /api/organisations/:id/members    (super_admin OR tenant_admin of that org)
 *
 * Backend payloads use snake_case (legacy convention preserved in
 * server/src/adapters/inbound/http/admin/organisations.routes.ts and
 * server/src/adapters/inbound/http/organisations/members.routes.ts).
 * Schemas here parse that wire format directly. A future migration to
 * camelCase happens with ADR-010 (god-endpoint suppression).
 */

// ── Organisations ────────────────────────────────────────────────────────

const OrganisationRowSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  company_name: z.string(),
  contact_name: z.string(),
  is_active: z.boolean(),
  // SU-1 (W7): deleted (soft) wins over disabled; an older API reads from is_active.
  status: z.enum(['active', 'disabled', 'deleted']).optional(),
  deleted_at: z.string().nullable().optional(),
  created_date: z.coerce.date(),
  updated_date: z.coerce.date(),
  // SR2-7: stored flags; an older API without the field reads as off.
  ui_flags: z
    .object({ ui_refonte: z.boolean(), call_page: z.boolean() })
    .default({ ui_refonte: false, call_page: false }),
  // W6: usage columns. null = could not be read (« Indisponible », DES-19);
  // an older API without the field reads as unknown, never as 0.
  usage: z
    .object({
      members: z.number().int().nullable(),
      numbers: z.number().int().nullable(),
      calls_30d: z.number().int().nullable(),
      last_call_at: z.string().nullable(),
    })
    .default({ members: null, numbers: null, calls_30d: null, last_call_at: null }),
});

export type OrganisationRow = z.infer<typeof OrganisationRowSchema>;

const ListOrganisationsEnvelopeSchema = z.object({
  organisations: z.array(z.unknown()),
  total: z.number().int().nonnegative(),
});

export async function listOrganisations(options?: { signal?: AbortSignal }): Promise<{
  organisations: OrganisationRow[];
  total: number;
  invalid: number;
}> {
  const raw = await apiClient.get<unknown>('/admin/organisations', options);
  const envelope = ListOrganisationsEnvelopeSchema.parse(raw);

  // Per-row safeParse so a single corrupted DB record (e.g. email='undefined'
  // legacy seed) does not blank out the entire list. We log the bad rows for
  // diagnosis and surface a count to the UI.
  const valid: OrganisationRow[] = [];
  let invalid = 0;
  for (let i = 0; i < envelope.organisations.length; i++) {
    const result = OrganisationRowSchema.safeParse(envelope.organisations[i]);
    if (result.success) {
      valid.push(result.data);
    } else {
      invalid++;
      // eslint-disable-next-line no-console
      console.warn('[listOrganisations] dropping malformed row', {
        index: i,
        raw: envelope.organisations[i],
        issues: result.error.issues,
      });
    }
  }

  return { organisations: valid, total: envelope.total, invalid };
}

/**
 * W6 (B3-AdminOrganisations): creation by invitation. The super admin never
 * types a password for someone else: the first admin receives the welcome
 * email with a link to choose theirs.
 */
export interface CreateOrganisationInput {
  email: string;
  companyName: string;
  contactName: string;
  adminUser: {
    email: string;
    fullName: string;
  };
}

const CreateOrganisationResponseSchema = z.object({
  organisation: z.object({ id: z.string(), email: z.string(), company_name: z.string() }),
  admin: z.object({ id: z.string(), email: z.string() }),
  invited: z.boolean().default(false),
  /** REV-W6-S4: whether the welcome email left (absent from an older server: assumed sent). */
  email_sent: z.boolean().optional(),
});

export type CreatedOrganisation = z.infer<typeof CreateOrganisationResponseSchema>;

export async function createOrganisation(input: CreateOrganisationInput): Promise<CreatedOrganisation> {
  const raw = await apiClient.post<unknown>('/admin/organisations', input);
  return CreateOrganisationResponseSchema.parse(raw);
}

export interface OrganisationUiFlags { ui_refonte: boolean; call_page: boolean }

const UiFlagsResponseSchema = z.object({ ui_flags: z.object({ ui_refonte: z.boolean(), call_page: z.boolean() }) });

/** PATCH /api/admin/organisations/:id/ui-flags (SR2-7). Users see it at their next page load. */
export async function updateOrganisationUiFlags(id: string, patch: Partial<OrganisationUiFlags>): Promise<OrganisationUiFlags> {
  const raw = await apiClient.patch<unknown>(`/admin/organisations/${encodeURIComponent(id)}/ui-flags`, patch);
  return UiFlagsResponseSchema.parse(raw).ui_flags;
}

// ── Members ──────────────────────────────────────────────────────────────

const MemberRowSchema = z.object({
  membership_id: z.string(),
  user_id: z.string(),
  email: z.string().email(),
  username: z.string(),
  full_name: z.string(),
  role: RoleSchema,
  // ADR-027 — non-empty only when role === 'viewer' with explicit scope.
  customer_ids: z.array(z.string()).default([]),
  is_active: z.boolean(),
  created_date: z.coerce.date(),
});

export type MemberRow = z.infer<typeof MemberRowSchema>;

const ListMembersResponseSchema = z.object({
  members: z.array(MemberRowSchema),
});

export async function listMembers(
  organisationId: string,
  options?: { signal?: AbortSignal },
): Promise<MemberRow[]> {
  const raw = await apiClient.get<unknown>(
    `/organisations/${organisationId}/members`,
    options,
  );
  return ListMembersResponseSchema.parse(raw).members;
}

const MembersPageSchema = z.object({
  members: z.array(MemberRowSchema),
  total: z.number().int().nonnegative(),
  by_role: z.object({ tenant_admin: z.number().int(), caller: z.number().int(), viewer: z.number().int() }),
});

export type MembersPage = z.infer<typeof MembersPageSchema>;

/** W6 (SR1-1.2): one page of members; `total` and `by_role` count the whole organisation. */
export async function listMembersPage(
  organisationId: string,
  page: { limit: number; offset: number },
  options?: { signal?: AbortSignal },
): Promise<MembersPage> {
  const raw = await apiClient.get<unknown>(`/organisations/${organisationId}/members`, {
    ...options,
    query: { limit: page.limit, offset: page.offset },
  });
  return MembersPageSchema.parse(raw);
}

export interface InviteMemberInput {
  email: string;
  role: Role;
  /** Used only when the email has no account yet and one is created. */
  full_name?: string;
}

/** Returns true when the account was created (a welcome email was sent). */
export async function inviteMember(
  organisationId: string,
  input: InviteMemberInput,
): Promise<boolean> {
  const raw = await apiClient.post<{ created?: boolean }>(
    `/organisations/${organisationId}/members/invite`,
    input,
  );
  return raw?.created === true;
}

export async function changeMemberRole(
  organisationId: string,
  membershipId: string,
  role: Role,
): Promise<void> {
  await apiClient.patch<unknown>(
    `/organisations/${organisationId}/members/${membershipId}`,
    { role },
  );
}

// ADR-027 — set the per-Customer scope of a viewer Membership.
export async function setMemberCustomerIds(
  organisationId: string,
  membershipId: string,
  customerIds: string[],
): Promise<void> {
  await apiClient.patch<unknown>(
    `/organisations/${organisationId}/members/${membershipId}`,
    { customerIds },
  );
}

export async function removeMember(
  organisationId: string,
  membershipId: string,
): Promise<void> {
  await apiClient.delete<unknown>(
    `/organisations/${organisationId}/members/${membershipId}`,
  );
}

// The audit log and the broadcast notifications: src/api/endpoints/backoffice.ts (W6).
